import { type PropsWithChildren, useEffect, useRef, useState } from 'react'

import { useAuthenticationContext } from '../context/authenticationContext'
import { useMsal } from '../hooks/useMsal/useMsal'
import { OpenAPI as MsGraphOpenapi } from '../msGraph/openapi'
import type { EnvironmentSettings } from '../settings/fetchAppSettings'
import { setupServices } from '../utility/setupServices'
import type { OpenApiType } from '../utility/types'

type Props = PropsWithChildren<{
  environmentSettings: EnvironmentSettings
  openApi: OpenApiType
}>

export const SetHeaders = ({
  environmentSettings,
  openApi,
  children,
}: Props) => {
  const { instance, accounts } = useMsal()
  const isSetupDone = useRef(false)
  const [isSetupDoneState, setIsSetupDone] = useState(isSetupDone.current)
  const { setShouldShowUserInfo } = useAuthenticationContext()

  useEffect(() => {
    const handleSetup = async () => {
      if (accounts && accounts.length > 0 && !isSetupDone.current) {
        setupServices({
          clientApplication: instance,
          environmentSettings,
          services: openApi,
          accountInfo: accounts[0],
        })
        // Making an request for a token in order to be able to read user roles
        await Promise.allSettled(
          Object.values(
            environmentSettings?.REACT_APP_SHOWUSERINFO
              ? { ...openApi, msGraph: [MsGraphOpenapi] }
              : openApi,
          ).map((openAPIConfigs) => {
            // The openAPIConfigs array shares the same token, therefor we can just take the first one
            const { TOKEN, BASE } = openAPIConfigs[0] ?? {}

            return typeof TOKEN === 'function'
              ? TOKEN({ method: 'GET', url: BASE })
              : Promise.resolve(undefined)
          }),
        )

        if (environmentSettings?.REACT_APP_SHOWUSERINFO) {
          const sessionStorageValues = Object.values(sessionStorage)

          const canSeeUserInfo = sessionStorageValues.some((cachedKey) => {
            if (typeof cachedKey !== 'string') {
              return
            }

            let parsedValue: { target?: string } | undefined = undefined

            // The session storage key might not be parseable, therefor we just return if it doesn't match our expected format
            try {
              parsedValue = JSON.parse(cachedKey) as { target?: string }
            } catch {
              return
            }

            return parsedValue?.target?.includes('User.Read')
          })
          setShouldShowUserInfo(canSeeUserInfo)
        }

        setIsSetupDone(true)
        isSetupDone.current = true
      }
    }
    void handleSetup()
  }, [accounts, environmentSettings, instance, openApi, setShouldShowUserInfo])

  return <>{isSetupDoneState && children}</>
}
